import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Stack,
  Table,
  Title,
  Anchor,
  Switch,
} from "@mantine/core";

import {
  ADMIN_ORGANIZATION,
  PRONOSTICSV2_CREATE_AUTHORISED_ORGANIZATIONS,
  PRONOSTICSV2_CREATE_AUTHORISED_ROLES,
  PRONOSTICSV2_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  PRONOSTICSV2_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";

import type { PronosticV2 } from "pronosticsv2/types/pronosticv2.model";
import { AccessRights } from "common/admin/auth/AccessRights";
import { useOrganization } from "common/admin/auth/hooks";
import QueryWrapper from "common/components/QueryWrapper";
import { Th } from "common/components/table/Th";

import {
  useListPronosticV2sQuery,
  useRemovePronosticV2Mutation,
} from "pronosticsv2/api/pronosticv2.api";

import { Pronosticv2AdminRow } from "../components/Pronosticv2AdminRow";
import { ADMIN_PATH } from "_urls";

type SortedKeys = keyof Pick<PronosticV2, "owner" | "name" | "createdAt">;

function sortData(
  data: PronosticV2[],
  payload: { sortBy: SortedKeys; reversed: boolean },
): PronosticV2[] {
  return [...data].sort((a, b) => {
    const aValue = String(a[payload.sortBy]);
    const bValue = String(b[payload.sortBy]);
    return payload.reversed
      ? bValue.localeCompare(aValue)
      : aValue.localeCompare(bValue);
  });
}

export function Pronosticsv2ListAdminView() {
  const navigate = useNavigate();
  const listPronosticV2sQuery = useListPronosticV2sQuery();
  const [sortedData, setSortedData] = useState<PronosticV2[]>([]);
  const [sortBy, setSortBy] = useState<SortedKeys>("createdAt");
  const [reverseSortDirection, setReverseSortDirection] = useState(true);
  const [checked, setChecked] = useState(false);
  const [organization] = useOrganization();
  const [deletePronosticV2] = useRemovePronosticV2Mutation();

  const pronostics = useMemo(
    () => listPronosticV2sQuery.data,
    [listPronosticV2sQuery.data],
  );

  const filteredPronostics = useMemo(
    () =>
      (pronostics || []).filter(
        (pronostic) => pronostic.createdAt && pronostic.name,
      ),
    [pronostics],
  );

  const handleDelete = async (id: string) => {
    try {
      await deletePronosticV2(id).unwrap();
      console.log(`Pronostic ${id} deleted successfully`);
    } catch (error) {
      console.error("Failed to delete Pronostic", error);
    }
  };

  // const triggerSortingData = useCallback(
  //   (field: SortedKeys) => {
  //     if (pronostics) {
  //       const reversed = field === sortBy ? !reverseSortDirection : false;
  //       return sortData(pronostics, { sortBy: field, reversed });
  //     }
  //     return [];
  //   },
  //   [pronostics, reverseSortDirection, sortBy],
  // );

  useEffect(() => {
    const visiblePronostics = checked
      ? filteredPronostics
      : filteredPronostics.filter((p) => p.status !== "archived");

    const sorted = sortData(visiblePronostics, {
      sortBy,
      reversed: reverseSortDirection,
    });

    setSortedData(sorted);
  }, [filteredPronostics, sortBy, reverseSortDirection, checked]);

  return (
    <Box>
      <div
        className="sticky top-0 z-50 pb-4"
        style={{ backgroundColor: "#E9ECEF" }}
      >
        <Title order={3} align="center" m="xs">
          Pronostics
        </Title>
        <Breadcrumbs separator="→">
          <Anchor component={Link} to={`/${ADMIN_PATH}`}>
            Admin
          </Anchor>
          <span>Pronostics</span>
        </Breadcrumbs>

        <AccessRights
          authorizedOrganizations={PRONOSTICSV2_CREATE_AUTHORISED_ORGANIZATIONS}
          authorizedRoles={PRONOSTICSV2_CREATE_AUTHORISED_ROLES}
        >
          <Group position="right">
            <Button className="bg-button-admin" component={Link} to="new">
              New
            </Button>
          </Group>
          <Group position="right">
            <Switch
              checked={checked}
              onChange={(event) => setChecked(event.currentTarget.checked)}
              label="Archived"
            />
          </Group>
        </AccessRights>
      </div>

      <Card shadow="sm" p="xl" className="overflow-visible">
        <QueryWrapper query={listPronosticV2sQuery}>
          {filteredPronostics && filteredPronostics.length > 0 ? (
            <Table highlightOnHover>
              <thead>
                <tr>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "name"}
                    onClick={() =>
                      setSortBy((prev) => {
                        setReverseSortDirection(
                          prev === "name" ? !reverseSortDirection : false,
                        );
                        return "name";
                      })
                    }
                  >
                    Name
                  </Th>
                  <AccessRights
                    authorizedOrganizations={
                      PRONOSTICSV2_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                    }
                    authorizedRoles={
                      PRONOSTICSV2_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                    }
                  >
                    <Th
                      reversed={reverseSortDirection}
                      sorted={sortBy === "owner"}
                      onClick={() =>
                        setSortBy((prev) => {
                          setReverseSortDirection(
                            prev === "owner" ? !reverseSortDirection : false,
                          );
                          return "owner";
                        })
                      }
                    >
                      Organization
                    </Th>
                  </AccessRights>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "createdAt"}
                    onClick={() =>
                      setSortBy((prev) => {
                        setReverseSortDirection(
                          prev === "createdAt" ? !reverseSortDirection : false,
                        );
                        return "createdAt";
                      })
                    }
                  >
                    Creation Date
                  </Th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((pronostic) =>
                  pronostic.owner.L1 === organization ||
                  organization === ADMIN_ORGANIZATION ? (
                    <Pronosticv2AdminRow
                      key={pronostic.id}
                      pronosticv2={pronostic}
                      onEdit={() => navigate(pronostic.id)}
                      onDelete={() => handleDelete(pronostic.id)}
                      onDuplicate={() => navigate(`${pronostic.id}/duplicate`)}
                    />
                  ) : null,
                )}
              </tbody>
            </Table>
          ) : (
            <Stack align="center">
              <Box>No pronostics found</Box>
              <Button className="bg-button-admin" component={Link} to="new">
                New
              </Button>
            </Stack>
          )}
        </QueryWrapper>
      </Card>
    </Box>
  );
}
